"use client";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost, uploadFile } from "@/lib/api-client";
import type { ProductImage } from "@/lib/api-client";
import { Trash2, ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useRef, useState } from "react";
import { useI18n } from "@/lib/i18n/provider";

export function ImageManagerModal({
  productId,
  open,
  onOpenChange,
}: {
  productId: string | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const { t } = useI18n();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["images", productId],
    queryFn: () => apiGet<{ images: ProductImage[] }>(`/api/products/${productId}/images`),
    enabled: !!productId && open,
  });

  const images = data?.images ?? [];

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !productId) return;
    setUploading(true);
    try {
      const up = await uploadFile("/api/upload", file);
      await apiPost(`/api/products/${productId}/images`, {
        url: up.url,
        caption: caption || null,
      });
      toast.success(t("toast.photoAdded"));
      setCaption("");
      qc.invalidateQueries({ queryKey: ["images", productId] });
      qc.invalidateQueries({ queryKey: ["product", productId] });
      qc.invalidateQueries({ queryKey: ["products"] });
    } catch (err: any) {
      toast.error(err.message || t("toast.uploadFailed"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id: string) => {
    if (!productId) return;
    try {
      await apiDelete(`/api/products/${productId}/images/${id}`);
      toast.success(t("toast.photoRemoved"));
      qc.invalidateQueries({ queryKey: ["images", productId] });
      qc.invalidateQueries({ queryKey: ["product", productId] });
      qc.invalidateQueries({ queryKey: ["products"] });
    } catch (err: any) {
      toast.error(err.message || t("toast.couldNotRemove"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5" /> {t("images.title")}
          </DialogTitle>
          <DialogDescription>{t("images.description")}</DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border p-3">
          <div className="grid gap-2">
            <Input
              placeholder={t("images.caption")}
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              disabled={uploading}
            />
            <div className="flex items-center gap-3">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={onFile}
                disabled={uploading || !productId}
                className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-4 file:py-2 file:text-primary-foreground hover:file:bg-primary/90"
              />
              {uploading && <Loader2 className="h-4 w-4 animate-spin" />}
            </div>
          </div>
        </div>

        <div>
          <h4 className="mb-2 text-sm font-semibold">{t("images.photos", { count: images.length })}</h4>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
          ) : images.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("images.empty")}</p>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {images.map((im, i) => (
                <div
                  key={im.id}
                  className="group relative aspect-[4/3] overflow-hidden rounded-lg border bg-muted"
                >
                  { }
                  <img src={im.url} alt={im.caption || ""} className="h-full w-full object-cover" />
                  {i === 0 && (
                    <span className="absolute left-1 top-1 rounded bg-background/90 px-2 py-0.5 text-[10px] font-medium">
                      {t("images.cover")}
                    </span>
                  )}
                  <button
                    onClick={() => remove(im.id)}
                    className="absolute right-1 top-1 grid h-7 w-7 place-items-center rounded-md bg-background/90 text-muted-foreground opacity-0 transition group-hover:opacity-100 hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
